import { Component } from '@theme/component';
import { SlideshowSelectEvent, ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { getIntersectionRoot } from '@theme/scroll-container';
import { scrollIntoView } from '@theme/scrolling';
import { debounce, prefersReducedMotion } from '@theme/utilities';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 * @property {HTMLButtonElement[]} [stackThumbnails] - Stack mode thumbnail buttons.
 * @property {HTMLElement} [stackThumbnailsViewport] - Stack mode thumbnail scroll container.
 * @property {HTMLButtonElement} [stackThumbPrev] - Stack mode previous thumbnail arrow.
 * @property {HTMLButtonElement} [stackThumbNext] - Stack mode next thumbnail arrow.
 * @property {HTMLElement} [stackProgressFill] - Stack mode mobile progress bar fill.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  /** @type {IntersectionObserver | null} */
  #stackObserver = null;

  /** @type {Map<Element, number>} */
  #stackIntersectionRatios = new Map();

  /** @type {boolean} */
  #isStackScrolling = false;

  /** @type {number | undefined} */
  #stackScrollTimeout;

  /** @type {MediaQueryList} */
  #stackDesktopQuery = window.matchMedia('(min-width: 750px)');

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.presentation === 'stack') {
      this.#configureStackPresentation();
      this.#stackDesktopQuery.addEventListener('change', this.#configureStackPresentation);
      this.refs.stackThumbnailsViewport?.addEventListener('scroll', this.#updateStackThumbArrows, {
        passive: true,
        signal,
      });
      this.addEventListener(SlideshowSelectEvent.eventName, this.#handleStackSlideshowSelect, { signal });
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#stackDesktopQuery.removeEventListener('change', this.#configureStackPresentation);
    this.#stackObserver?.disconnect();
    this.#stackObserver = null;
    clearTimeout(this.#stackScrollTimeout);
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    if (this.presentation === 'stack') {
      if (this.#stackDesktopQuery.matches) {
        this.stackSelect(event.detail.index);
      } else {
        this.slideshow?.select(event.detail.index, undefined, { animate: false });
        this.#updateStackProgress(event.detail.index);
      }
      return;
    }

    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Syncs the mobile progress bar when the slideshow changes.
   *
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleStackSlideshowSelect = (event) => {
    if (this.presentation !== 'stack' || this.#stackDesktopQuery.matches) return;

    this.#updateStackProgress(event.detail.index);
  };

  /**
   * Switches between desktop scroll-sync and mobile slideshow behavior.
   */
  #configureStackPresentation = () => {
    if (this.#stackDesktopQuery.matches) {
      this.#initStackGallery();
      return;
    }

    this.#stackObserver?.disconnect();
    this.#stackObserver = null;
    this.#stackIntersectionRatios.clear();
    this.#updateStackProgress(this.slideshow?.current ?? 0);
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  /**
   * Initializes scroll-sync behavior for stack presentation on desktop.
   */
  #initStackGallery() {
    this.#stackObserver?.disconnect();
    this.#stackObserver = null;
    this.#stackIntersectionRatios.clear();

    const media = this.refs.media;
    if (!media?.length) return;

    const root = getIntersectionRoot();

    this.#stackObserver = new IntersectionObserver(
      (entries) => {
        if (this.#isStackScrolling) return;

        for (const entry of entries) {
          this.#stackIntersectionRatios.set(entry.target, entry.intersectionRatio);
        }

        let bestElement = null;
        let bestRatio = 0;

        for (const [element, ratio] of this.#stackIntersectionRatios) {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestElement = element;
          }
        }

        if (!bestElement) return;

        const index = media.indexOf(/** @type {HTMLElement} */ (bestElement));
        if (index >= 0) {
          this.#setActiveStackIndex(index, { scrollThumbnail: true });
        }
      },
      {
        root,
        threshold: Array.from({ length: 21 }, (_, index) => index / 20),
        rootMargin: '-35% 0px -35% 0px',
      }
    );

    media.forEach((element) => this.#stackObserver?.observe(element));
    this.#updateStackThumbArrows();
  }

  updatedCallback() {
    super.updatedCallback();

    if (this.presentation !== 'stack') return;

    this.#configureStackPresentation();
  }

  /**
   * Scrolls to a media item in stack presentation.
   *
   * @param {number} index - The media index to scroll to.
   * @param {Event} [event] - The triggering event.
   */
  stackSelect(index, event) {
    event?.preventDefault?.();

    if (!this.#stackDesktopQuery.matches) {
      this.slideshow?.select(index, event, { animate: !prefersReducedMotion() });
      this.#updateStackProgress(index);
      return;
    }

    const media = this.refs.media?.[index];
    if (!media) return;

    this.#isStackScrolling = true;
    clearTimeout(this.#stackScrollTimeout);

    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';

    scrollIntoView(media, {
      behavior,
      block: 'center',
    });

    this.#setActiveStackIndex(index, { scrollThumbnail: true });

    this.#stackScrollTimeout = window.setTimeout(() => {
      this.#isStackScrolling = false;
    }, 600);
  }

  /** Scrolls the thumbnail list up. */
  stackThumbPrev() {
    this.#scrollStackThumbnails(-1);
  }

  /** Scrolls the thumbnail list down. */
  stackThumbNext() {
    this.#scrollStackThumbnails(1);
  }

  /**
   * Scrolls the thumbnail viewport by one page of visible thumbnails.
   *
   * @param {number} direction - Scroll direction, -1 for up and 1 for down.
   */
  #scrollStackThumbnails(direction) {
    const viewport = this.refs.stackThumbnailsViewport;
    const firstThumbnail = this.refs.stackThumbnails?.[0];

    if (!viewport || !firstThumbnail) return;

    const styles = getComputedStyle(firstThumbnail.parentElement ?? firstThumbnail);
    const gap = parseFloat(styles.rowGap || styles.gap || '0') || 0;
    const step = (firstThumbnail.offsetHeight + gap) * 3;

    viewport.scrollBy({
      top: direction * step,
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
    });
  }

  /**
   * Updates the active thumbnail and arrow states.
   *
   * @param {number} index - The active media index.
   * @param {{ scrollThumbnail?: boolean }} [options] - Update options.
   */
  #setActiveStackIndex(index, { scrollThumbnail = false } = {}) {
    const thumbnails = this.refs.stackThumbnails;

    thumbnails?.forEach((button, buttonIndex) => {
      button.setAttribute('aria-selected', buttonIndex === index ? 'true' : 'false');
    });

    if (scrollThumbnail) {
      const activeThumbnail = thumbnails?.[index];
      const viewport = this.refs.stackThumbnailsViewport;

      if (activeThumbnail && viewport) {
        scrollIntoView(activeThumbnail, {
          ancestor: viewport,
          behavior: prefersReducedMotion() ? 'instant' : 'smooth',
          block: 'nearest',
        });
      }
    }

    this.#updateStackProgress(index);
    this.#updateStackThumbArrows();
  }

  /**
   * Updates the mobile progress bar fill width.
   *
   * @param {number} index - The active media index.
   */
  #updateStackProgress(index) {
    const fill = this.refs.stackProgressFill;
    if (!fill) return;

    fill.style.setProperty('--stack-active-index', String(index));
  }

  /** Updates disabled state for stack thumbnail arrows. */
  #updateStackThumbArrows = debounce(() => {
    const viewport = this.refs.stackThumbnailsViewport;
    const prev = this.refs.stackThumbPrev;
    const next = this.refs.stackThumbNext;

    if (!viewport || !prev || !next) return;

    prev.disabled = viewport.scrollTop <= 1;
    next.disabled = viewport.scrollTop + viewport.clientHeight >= viewport.scrollHeight - 1;
  }, 50);

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
